import React, { useState } from "react";
import { CreditModalProps } from "../../utils/types";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { User } from "lucide-react";

const CreditModal: React.FC<CreditModalProps> = ({ order, paymentMethod, handleCloseModal, handleConfirmPayment }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // All hooks must be called unconditionally at the top
  const carNumbers = React.useMemo(() => {
    if (!order) return [];
    
    const cars: string[] = [];
    const orderDetails = order?.orderDetails?.[0];
    const products = orderDetails?.products || [];
    
    products.forEach((product: any) => {
      const services = product?.service || [];
      services.forEach((service: any) => {
        if (service?.carNumber) {
          cars.push(service.carNumber);
        }
      });
    });
    
    return Array.from(new Set(cars)); // Remove duplicates
  }, [order]);

  const totalCars = carNumbers.length;
  const carNumbersText = carNumbers.join(", ") || "N/A";
  
  // Get remaining amount from the latest order transaction
  const totalAmount = React.useMemo(() => {
    if (!order) return 0;
    return order.orders?.[0]?.remainingAmount || order.totalOutstandingBalance || 0;
  }, [order]);

  // Get customer name from customerDetails
  const customerName = React.useMemo(() => {
    if (!order?.customerDetails) return "N/A";
    return `${order.customerDetails.firstName} ${order.customerDetails.lastName || ''}`.trim();
  }, [order]);

  // Get received amount from paymentHistory
  const receivedAmount = React.useMemo(() => {
    if (!order?.paymentHistory) return 0;
    return order.paymentHistory.reduce((total: number, payment: any) => 
      total + (payment.receivedAmount || 0), 0) || 0;
  }, [order]);

  // Calculate total order amount
  const totalOrderAmount = React.useMemo(() => {
    if (!order) return 0;
    return order.orders?.[0]?.finalAmount || 
           order.orders?.[0]?.orderAmount || 
           0;
  }, [order]);

  // Get order date
  const orderDate = React.useMemo(() => {
    if (!order?.orders?.[0]?.orderedOn?.$date) return "N/A";
    return new Date(order.orders[0].orderedOn.$date).toLocaleDateString();
  }, [order]);

  const handleMouseEnter = () => {
    if (totalCars > 0) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleCompletePayment = async () => {
    setIsProcessing(true);
    try {
      await handleConfirmPayment(paymentMethod);
      // Modal will be closed by the parent component after successful payment
    } catch (error) {
      console.error("Payment failed:", error);
      setIsProcessing(false);
    }
  };

  // Early return after all hooks
  if (!order) return null;

  return (
    <ModalWrapper title="Payment Summary" onClose={handleCloseModal} width="max-w-lg">
      <div className="space-y-5 text-sm text-gray-700">
        {/* Header Section */}
        <div className="bg-gray-50 border rounded-lg p-4 flex items-center gap-3">
          <div className="flex-shrink-0 bg-blue-100 p-2 rounded-full">
            <User className="text-blue-600" size={24} />
          </div>

          <div className="flex flex-col">
            <div className="font-semibold text-gray-900">
              Customer: <span className="text-gray-800">{customerName}</span>
            </div>
            <div className="text-sm text-gray-600">
              Payment: <span className="capitalize">{paymentMethod}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Date: {orderDate}
            </div>
          </div>
        </div>

        {/* Order Summary Section */}
        <div>
          <h3 className="font-semibold text-gray-800 mb-2">Order Summary</h3>
          <hr className="border-gray-200 mb-2" />

          <div className="grid grid-cols-2 gap-y-2">
            <div>Order ID</div>
            <div className="text-right font-medium text-gray-900">{order.orderId || "N/A"}</div>

            <div>Car Number</div>
            <div 
              className="text-right relative cursor-help"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <span className="font-medium text-gray-900">
                {totalCars} car{totalCars !== 1 ? "s" : ""}
              </span>

              {/* Tooltip */}
              {showTooltip && totalCars > 0 && (
                <div className="absolute z-50 px-3 py-2 text-xs bg-gray-900 text-white rounded-md shadow-lg max-w-xs break-words"
                  style={{
                    top: "100%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    marginTop: "8px",
                  }}
                >
                  <div className="font-semibold mb-1">Car Numbers:</div>
                  <div>{carNumbersText}</div>
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              )}
            </div>

            <div>Order Amount</div>
            <div className="text-right">{totalOrderAmount} QAR</div>

            <div>Received Amount</div>
            <div className="text-right">{receivedAmount} QAR</div>

            <div>Remaining Amount</div>
            <div className="text-right font-semibold text-gray-900">{totalAmount} QAR</div>

            <div>Payment Type</div>
            <div className="text-right capitalize">{paymentMethod}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 border-t pt-4 mt-3">
          <button 
            onClick={handleCloseModal} 
            disabled={isProcessing}
            className="px-5 py-2 rounded-full border border-blue-500 text-blue-500 hover:bg-blue-50 font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button 
            onClick={handleCompletePayment} 
            disabled={isProcessing}
            className="px-5 py-2 rounded-full font-medium bg-blue-500 text-white hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? "Processing..." : "Complete"}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default CreditModal;